import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';
import { MAX_KOD, oczysc, powodOdrzucenia } from '@/lib/kod-sps';

/**
 * POLE KODU SPS-a — z odmową PRZY PISANIU i dymkiem, który mówi dlaczego.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „nie pozwalało wpisać więcej niż 32 znaki i niech wyskakuje dymek,
 *  że maks 32 znaki… nie może być też w kodzie grupy polskich znaków (nie pozwalaj ich wpisać
 *  z klawiatury)". Ten sam wymóg dotyczy kodu katalogowego artykułu.
 *
 *  ⚠️ JEDEN KOMPONENT NA DWA OKNA, bo to jedno pole o jednej regule. Napisane osobno w grupach
 *  i w artykułach rozjechałoby się przy pierwszej poprawce — a rozjazd byłby widoczny dopiero
 *  w bazie klienta, na tym z dwóch ekranów, o którym ktoś zapomniał.
 *
 *  ⚠️ DYMEK GAŚNIE SAM. Komunikat, który zostaje na ekranie po poprawieniu wpisu, po chwili
 *  przestaje znaczyć „teraz", a zaczyna znaczyć „kiedyś" — i człowiek uczy się go nie czytać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const DYMEK_MS = 2500;

export function PoleKoduSps({
  id,
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const { t } = useI18n();
  const [dymek, setDymek] = useState<'DLUGOSC' | 'ZNAKI' | null>(null);

  useEffect(() => {
    if (dymek === null) return;
    const id = setTimeout(() => setDymek(null), DYMEK_MS);
    return () => clearTimeout(id);
  }, [dymek]);

  return (
    <>
      <Input
        id={id}
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder}
        /**
         * ⚠️⚠️ `maxLength` NIE WYSTARCZA, i to jest sedno tego komponentu. Przeglądarka ucina
         * nadmiar MILCZĄCO — człowiek wkleja kod z arkusza, dostaje 32 pierwsze znaki i nie wie,
         * że coś zniknęło. Limit zostaje jako druga linia obrony, ale o odrzuceniu mówi dymek.
         */
        maxLength={MAX_KOD}
        value={value}
        onChange={(e) => {
          const surowy = e.target.value;
          setDymek(powodOdrzucenia(surowy));
          /**
           * ⚠️ CZYŚCIMY WARTOŚĆ, NIE ZDARZENIA KLAWIATURY. Blokada pojedynczych klawiszy nie
           * dotyczy `Ctrl+V`, a kody najczęściej się wkleja — razem z polskimi literami i spacją
           * na końcu.
           */
          onChange(oczysc(surowy));
        }}
      />
      <p className="mt-1 text-xs text-muted-foreground">{t('kod.reguly')}</p>
      {dymek !== null && (
        <p className="mt-1 text-xs text-[var(--warning)]" role="status">
          {t(dymek === 'DLUGOSC' ? 'kod.zaDlugi' : 'kod.zleZnaki')}
        </p>
      )}
    </>
  );
}
